import { useMemo } from "react";

import { FilterItem, KeyedArray } from "@repo/models";

import { GridFiltersController } from "../api/gridFiltersController";
import { useFilterItemQuery } from "../api/queries";
import * as Queries from "../api/queries";
import { BranchFilterItem } from "../models/filterItem";

/**
 * Checks selected items, and filters other dropdown options based on selection (i.e. filters marketers based on channel)
 * Does this by checking which branches are associated with selected items
 */
export function useSegmentFilter<T = KeyedArray<FilterItem>>(
	segment: ISegment,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	const [brands, brandsLoaded] = useFilterItemQuery([Queries.BRANDS], GridFiltersController.getBrands);
	const [channels, channelsLoaded] = useFilterItemQuery([Queries.CHANNELS], GridFiltersController.getChannels);
	const [marketers, marketersLoaded] = useFilterItemQuery([Queries.MARKETERS], GridFiltersController.getMarketers);
	const [states, statesLoaded] = useFilterItemQuery([Queries.STATES], GridFiltersController.getStates);
	const [branches, branchesLoaded] = useFilterItemQuery<BranchFilterItem>(
		[Queries.BRANCHES],
		GridFiltersController.getBranches,
	);
	const filteredBranches = useMemo(
		() => getBranchesFromFilter(segment),
		[segment, branches, channels, marketers, states],
	);

	const activeSegmentLists = useMemo(() => {
		return {
			brands: getRelevantIdsFromBranches(filteredBranches, brands, (b) => b.brandId),
			channels: getRelevantIdsFromBranches(filteredBranches, channels, (b) => b.channelId),
			marketers: getRelevantIdsFromBranches(filteredBranches, marketers, (b) => b.marketerId),
			states: getRelevantIdsFromBranches(filteredBranches, states, (b) => b.stateId),
			branches: getRelevantIdsFromBranches(filteredBranches, branches, (b) => b.id),
			brandsLoaded,
			channelsLoaded,
			marketersLoaded,
			statesLoaded,
			branchesLoaded,
		} as ISegmentList<T>;
	}, [filteredBranches, channels, marketers, states, branches]);
	return activeSegmentLists;

	function getBranchesFromFilter(segment: ISegment) {
		segment = segment || ({} as ISegment);
		let filteredBranches = branches?.data;
		if (!branches?.any()) return filteredBranches ?? [];
		if (!segment.showInactiveOffices) filteredBranches = filteredBranches.filter((b) => b.Active ?? true);
		filteredBranches = filteredBranches
			.filter((b) => !segment.brandId || b.brandId === segment.brandId)
			.filter((b) => !segment.channelId || b.channelId === segment.channelId)
			.filter((b) => !segment.marketerId || b.marketerId === segment.marketerId)
			.filter((b) => !segment.stateId || b.stateId === segment.stateId);
		filteredBranches = filteredBranches
			.filter((b) => !segment.brandIds?.length || segment.brandIds.includes(b.brandId))
			.filter((b) => !segment.channelIds?.length || segment.channelIds.includes(b.channelId))
			.filter((b) => !segment.marketerIds?.length || segment.marketerIds.includes(b.marketerId))
			.filter((b) => !segment.stateIds?.length || segment.stateIds.includes(b.stateId));
		return filteredBranches;
	}

	function getRelevantIdsFromBranches(
		branches: BranchFilterItem[],
		allItems: KeyedArray<FilterItem>,
		selector: (b: BranchFilterItem) => number,
	) {
		const groups = branches.groupBy(selector);
		if (!allItems?.any()) return new KeyedArray<FilterItem>([], (k) => k.id);
		const relevantIds = allItems?.filter((i) => groups.has(i.id));
		if (mapItems) return mapItems(relevantIds);
		return relevantIds;
	}
}

export function useSegmentFilterCascaded<T = KeyedArray<FilterItem>>(
	segmentProp: ISegment,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	const segment = segmentProp || ({} as ISegment);
	const [brands, brandsLoaded] = useFilterItemQuery([Queries.BRANDS], GridFiltersController.getBrands);
	const [channels, channelsLoaded] = useFilterItemQuery([Queries.CHANNELS], GridFiltersController.getChannels);
	const [marketers, marketersLoaded] = useFilterItemQuery([Queries.MARKETERS], GridFiltersController.getMarketers);
	const [states, statesLoaded] = useFilterItemQuery([Queries.STATES], GridFiltersController.getStates);
	const [branches, branchesLoaded] = useFilterItemQuery<BranchFilterItem>(
		[Queries.BRANCHES],
		GridFiltersController.getBranches,
	);
	const filteredBranchesOnBrand = useMemo(
		() => getBranchesFromFilter({ brandId: segment.brandId, brandIds: segment.brandIds }, branches?.data ?? []),
		[segment, brands, branches],
	);
	const filteredBranchesOnChannel = useMemo(
		() =>
			getBranchesFromFilter(
				{ channelId: segment.channelId, channelIds: segment.channelIds },
				filteredBranchesOnBrand,
			),
		[segment, channels, filteredBranchesOnBrand],
	);
	const filteredBranchesOnMarketer = useMemo(
		() =>
			getBranchesFromFilter(
				{ marketerId: segment.marketerId, marketerIds: segment.marketerIds },
				filteredBranchesOnChannel,
			),
		[segment, marketers, filteredBranchesOnChannel],
	);
	const filteredBranchesOnState = useMemo(
		() =>
			getBranchesFromFilter({ stateId: segment.stateId, stateIds: segment.stateIds }, filteredBranchesOnMarketer),
		[segment, states, filteredBranchesOnMarketer],
	);
	const filteredBranches = useMemo(
		() => getBranchesFromFilter(segment, filteredBranchesOnState),
		[segment, channels, marketers, states, filteredBranchesOnState],
	);

	const activeSegmentLists = useMemo(() => {
		return {
			brands: brands,
			channels: getRelevantIdsFromBranches(filteredBranchesOnBrand, channels, (b) => b.channelId),
			marketers: getRelevantIdsFromBranches(filteredBranchesOnChannel, marketers, (b) => b.marketerId),
			states: getRelevantIdsFromBranches(filteredBranchesOnMarketer, states, (b) => b.stateId),
			branches: getRelevantIdsFromBranches(filteredBranchesOnState, branches, (b) => b.id),
			brandsLoaded,
			channelsLoaded,
			marketersLoaded,
			statesLoaded,
			branchesLoaded,
		} as ISegmentList<T>;
	}, [filteredBranches, channels, marketers, states, branches]);

	return activeSegmentLists;

	function getBranchesFromFilter(segmentPart: Partial<ISegment>, filteredBranches: BranchFilterItem[]) {
		if (!filteredBranches.length) return filteredBranches;
		if (!segment.showInactiveOffices) filteredBranches = filteredBranches.filter((b) => b.Active ?? true);
		filteredBranches = filteredBranches
			.filter((b) => !segmentPart.brandId || b.brandId === segmentPart.brandId)
			.filter((b) => !segmentPart.channelId || b.channelId === segmentPart.channelId)
			.filter((b) => !segmentPart.marketerId || b.marketerId === segmentPart.marketerId)
			.filter((b) => !segmentPart.stateId || b.stateId === segmentPart.stateId);
		filteredBranches = filteredBranches
			.filter((b) => !segmentPart.brandIds?.length || segmentPart.brandIds.includes(b.brandId))
			.filter((b) => !segmentPart.channelIds?.length || segmentPart.channelIds.includes(b.channelId))
			.filter((b) => !segmentPart.marketerIds?.length || segmentPart.marketerIds.includes(b.marketerId))
			.filter((b) => !segmentPart.stateIds?.length || segmentPart.stateIds.includes(b.stateId));
		return filteredBranches;
	}

	function getRelevantIdsFromBranches(
		branches: BranchFilterItem[],
		allItems: KeyedArray<FilterItem>,
		selector: (b: BranchFilterItem) => number,
	) {
		if (!branches.length) return mapItems ? mapItems(allItems) : allItems;
		const groups = branches.groupBy(selector);
		const relevantIds = allItems.filter((i) => groups.has(i.id));
		if (mapItems) return mapItems(relevantIds);
		return relevantIds;
	}
}

export type idType = string | number;

export interface ISegment {
	brandId?: idType;
	channelId?: idType;
	marketerId?: idType;
	stateId?: idType;
	branchId?: idType;

	showInactiveOffices?: boolean;

	brandIds?: idType[];
	channelIds?: idType[];
	marketerIds?: idType[];
	stateIds?: idType[];
	branchIds?: idType[];
}

export interface ISegmentList<T = KeyedArray<FilterItem>> {
	brands: T;
	channels: T;
	marketers: T;
	states: T;
	branches: T;
	brandsLoaded: boolean;
	channelsLoaded: boolean;
	marketersLoaded: boolean;
	statesLoaded: boolean;
	branchesLoaded: boolean;
}
